import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'
import { Preferences } from '@capacitor/preferences'

export type FontSize = 'small' | 'medium' | 'large'

interface FontSizeContextType {
  fontSize: FontSize
  loading: boolean
  setFontSize: (size: FontSize) => void
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined)

// ローカルストレージキー
const FONT_SIZE_KEY = 'user_font_size'

// フォントサイズをCSSカスタムプロパティとして適用
const applyFontSize = (size: FontSize) => {
  const root = document.documentElement

  switch (size) {
    case 'small':
      root.style.setProperty('--font-scale', '0.875')
      root.style.setProperty('--base-font-size', '14px')
      break
    case 'medium':
      root.style.setProperty('--font-scale', '1')
      root.style.setProperty('--base-font-size', '16px')
      break
    case 'large':
      root.style.setProperty('--font-scale', '1.125')
      root.style.setProperty('--base-font-size', '18px')
      break
  }
}

export function FontSizeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [fontSize, setFontSizeState] = useState<FontSize>('medium')
  const [loading, setLoading] = useState(true)

  // 初期ロード - ローカルストレージから読み込み
  useEffect(() => {
    loadLocalFontSize()
  }, [])

  // ユーザーログイン時にサーバーから読み込み
  useEffect(() => {
    if (user) {
      loadServerFontSize()
    }
  }, [user])

  // フォントサイズが変わったらCSSに適用
  useEffect(() => {
    applyFontSize(fontSize)
  }, [fontSize])

  const loadLocalFontSize = async () => {
    try {
      const { value } = await Preferences.get({ key: FONT_SIZE_KEY })
      if (value && ['small', 'medium', 'large'].includes(value)) {
        setFontSizeState(value as FontSize)
      }
    } catch (error) {
      console.error('Error loading local font size:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadServerFontSize = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('font_size')
        .eq('id', user?.id)
        .single()

      if (error) throw error

      if (data?.font_size && ['small', 'medium', 'large'].includes(data.font_size)) {
        const serverSize = data.font_size as FontSize
        setFontSizeState(serverSize)
        // ローカルにも保存
        await Preferences.set({ key: FONT_SIZE_KEY, value: serverSize })
      }
    } catch (error) {
      console.error('Error loading font size from server:', error)
    }
  }

  const setFontSize = async (size: FontSize) => {
    // 即座にUIを更新
    setFontSizeState(size)

    // ローカルに保存
    try {
      await Preferences.set({ key: FONT_SIZE_KEY, value: size })
    } catch (error) {
      console.error('Error saving local font size:', error)
    }

    // サーバーに保存（ログインしている場合）
    if (user) {
      try {
        await supabase
          .from('user_profiles')
          .update({ font_size: size })
          .eq('id', user.id)
      } catch (error) {
        console.error('Error saving font size to server:', error)
      }
    }
  }

  return (
    <FontSizeContext.Provider value={{ fontSize, loading, setFontSize }}>
      {children}
    </FontSizeContext.Provider>
  )
}

export function useFontSize() {
  const context = useContext(FontSizeContext)
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider')
  }
  return context
}
